// =====================================================================
// Faz AA (2026-05-07) — Geographic Analytics
// =====================================================================
// "Müşterilerim hangi şehirden geliyor?" sorusuna cevap.
// page_views.ip_address × ip_geo_cache JOIN ile coğrafi dağılım.
//
// ip_geo_cache services/ip-geo-resolver.js (30dk tick) tarafından doldurulur.
// İlk açılışta bazı IP'ler henüz çözülmemiş olabilir; coverage % gösterilir.
//
// Endpoint: GET /api/admin/insights/geo?days=N
//
// 4 panel:
//   1. KPI: çözülmüş IP sayısı + coverage %
//   2. Ülke listesi (top 10)
//   3. Türkiye şehir listesi (top 15) — flag/icon ile
//   4. Top ISP listesi (bot/proxy tespiti için)
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, ToggleButton,
  ToggleButtonGroup, LinearProgress
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PublicIcon from "@mui/icons-material/Public";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import RouterIcon from "@mui/icons-material/Router";
import API from "../services/api";

const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");

// Country code → emoji flag (ASCII offset trick — A=0x1F1E6)
// US → 🇺🇸, TR → 🇹🇷, etc. Çoğu modern tarayıcı destekler.
const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return "🌍";
  const codePoints = cc.toUpperCase()
    .split("")
    .map(c => 0x1F1E6 - 65 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

export default function GeoAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(30);

  const load = (d = days) => {
    setLoading(true);
    API.get(`/api/admin/insights/geo?days=${d}`)
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(days); }, [days]);

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const cov = data.coverage || {};
  const isLowCoverage = cov.pct < 50;

  // Ülke listesi için max view (bar genişliği için)
  const countryMax = Math.max(...(data.by_country || []).map(c => Number(c.views) || 0), 1);
  // Şehir için max
  const cityMax = Math.max(...(data.by_city || []).map(c => Number(c.views) || 0), 1);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Coğrafi Trafik</Typography>
          <Typography variant="body2" color="text.secondary">
            Hangi ülkeden, hangi şehirden ziyaret geliyor
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup size="small" value={days} exclusive onChange={(_, v) => v && setDays(v)}>
            <ToggleButton value={7}>7g</ToggleButton>
            <ToggleButton value={30}>30g</ToggleButton>
            <ToggleButton value={90}>90g</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Yenile">
            <IconButton onClick={() => load(days)}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Coverage Alert */}
      {isLowCoverage && cov.total_ips > 0 && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <strong>Coverage %{cov.pct}</strong> — {cov.unresolved} IP henüz coğrafi olarak çözülmedi.
          Worker (30dk tick) çalışıyor, eksik IP'ler kademeli olarak doluyor.
          1-2 saat sonra tam tablo görünür.
        </Alert>
      )}

      {/* KPI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam IP</Typography>
            <Typography variant="h4" fontWeight={800}>{fmtNum(cov.total_ips)}</Typography>
            <Typography variant="caption" color="text.disabled">unique ziyaretçi</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Çözülmüş</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{fmtNum(cov.resolved_ips)}</Typography>
            <Typography variant="caption" color="text.disabled">%{cov.pct} coverage</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Ülke Sayısı</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">{data.by_country?.length || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Türkiye Şehri</Typography>
            <Typography variant="h4" fontWeight={800} color="#f59e0b">{data.by_city?.length || 0}</Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Ülke listesi */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
              <PublicIcon sx={{ color: "#1565C0" }} />
              <Typography variant="h6" fontWeight={700}>Ülkeler (Top 10)</Typography>
            </Box>
            {data.by_country.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary" fontSize={13}>
                  Henüz çözümlenmiş IP yok — worker dolduruyor (30dk tick)
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell><strong>Ülke</strong></TableCell>
                    <TableCell align="right"><strong>Görüntüleme</strong></TableCell>
                    <TableCell align="right"><strong>Oturum</strong></TableCell>
                    <TableCell sx={{ width: 100 }}><strong>Pay</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.by_country.map((c, i) => {
                    const pct = (Number(c.views) / countryMax) * 100;
                    return (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography fontSize={20}>{flagEmoji(c.country_code)}</Typography>
                            <Typography fontSize={13} fontWeight={i < 3 ? 700 : 500}>
                              {c.country}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(c.views)}</TableCell>
                        <TableCell align="right">{fmtNum(c.sessions)}</TableCell>
                        <TableCell>
                          <Box sx={{ height: 6, bgcolor: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                            <Box sx={{
                              width: `${pct}%`, height: "100%",
                              bgcolor: c.country_code === "TR" ? "#ef4444" : "#1565C0",
                              transition: "width 0.3s"
                            }} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </Grid>

        {/* Türkiye şehirleri */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 2, bgcolor: "#fef2f2", borderBottom: "1px solid #fee2e2", display: "flex", alignItems: "center", gap: 1 }}>
              <LocationCityIcon sx={{ color: "#ef4444" }} />
              <Typography variant="h6" fontWeight={700}>🇹🇷 Türkiye Şehirleri</Typography>
            </Box>
            {data.by_city.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary" fontSize={13}>
                  Henüz Türkiye şehri datası yok
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell><strong>Şehir</strong></TableCell>
                    <TableCell align="right"><strong>Görüntüleme</strong></TableCell>
                    <TableCell align="right"><strong>Oturum</strong></TableCell>
                    <TableCell sx={{ width: 80 }}><strong>Pay</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.by_city.map((c, i) => {
                    const pct = (Number(c.views) / cityMax) * 100;
                    return (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Typography fontSize={13} fontWeight={i < 3 ? 700 : 500}>
                            {i < 3 && <span style={{ marginRight: 4 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}</span>}
                            {c.city}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" fontSize={10}>
                            {c.region}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(c.views)}</TableCell>
                        <TableCell align="right">{fmtNum(c.sessions)}</TableCell>
                        <TableCell>
                          <Box sx={{ height: 6, bgcolor: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                            <Box sx={{
                              width: `${pct}%`, height: "100%",
                              bgcolor: "#ef4444", transition: "width 0.3s"
                            }} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Top ISP — bot/proxy tespiti */}
      {data.top_isps && data.top_isps.length > 0 && (
        <Card sx={{ borderRadius: 3, mt: 3, overflow: "hidden" }}>
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
            <RouterIcon sx={{ color: "#64748b" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700}>İSS / Bağlantı Sağlayıcı</Typography>
              <Typography variant="caption" color="text.secondary">
                Bot / proxy tespiti için — şüpheli ISS'ler (Amazon, DigitalOcean, OVH) çoksa bot trafiği var demek
              </Typography>
            </Box>
          </Box>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>İSS</strong></TableCell>
                <TableCell align="right"><strong>Görüntüleme</strong></TableCell>
                <TableCell align="right"><strong>Unique IP</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.top_isps.slice(0, 10).map((i, idx) => {
                // Bot/cloud ISS detection — bunlar genelde bot/scraper kaynağı
                const isCloud = /amazon|digitalocean|ovh|google|microsoft|hetzner|linode|vultr/i.test(i.isp || "");
                return (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Typography fontSize={13} sx={{ color: isCloud ? "#f59e0b" : "inherit" }}>
                        {isCloud && "⚠️ "}{i.isp}
                      </Typography>
                      {isCloud && (
                        <Typography variant="caption" color="#f59e0b" fontSize={10}>
                          cloud/datacenter — muhtemelen bot
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(i.views)}</TableCell>
                    <TableCell align="right">{fmtNum(i.unique_ips)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Açıklama */}
      <Card sx={{ p: 2, borderRadius: 3, mt: 3, bgcolor: "#f8fafc" }}>
        <Typography variant="caption" color="text.secondary">
          💡 IP coğrafyası <strong>ip-api.com</strong> ücretsiz tier'ı ile çözülür (45 req/dk, IP başına).
          Worker 30 dakikada bir çalışır, yeni IP'leri çözer ve cache'ler.
          Aynı IP tekrar gelirse cache'den okunur (re-lookup yok).
          Free tier ticari kullanım için lisans gerek; trafik artarsa $13/ay paid tier'a geçilir.
        </Typography>
      </Card>
    </Box>
  );
}
